/**
 * App navigation components
 */

import { Settings } from 'lucide-react';
import React from 'react';

import { Button } from '@/components/ui/button';

export type Route =
  | 'home'
  | 'tools'
  | 'editor'
  | 'settings'
  | 'pipelines'
  | 'favorites'
  | 'seo';

export function normalizeRoute(pathname: string): Route {
  const pathOnly = pathname.split(/[?#]/)[0] ?? pathname;
  const withoutAliasPrefix = pathOnly.replace(/^\/test(\/|$)/, '/');
  const firstSegment = withoutAliasPrefix.split('/').filter(Boolean)[0] ?? 'home';

  switch (firstSegment) {
    case 'home':
      return 'home';
    case 'tools':
      return 'tools';
    case 'editor':
      return 'editor';
    case 'settings':
      return 'settings';
    case 'pipelines':
      return 'pipelines';
    case 'workflows':
      return 'pipelines'; // grace period: old URLs still work
    case 'favorites':
      return 'favorites';
    case 'seo':
      return 'seo';
    default:
      return 'home';
  }
}

/** Extract the second path segment for parameterised routes like /seo/:slug */
export function extractSlug(pathname: string): string | null {
  const pathOnly = pathname.split(/[?#]/)[0] ?? pathname;
  const segments = pathOnly.split('/').filter(Boolean);
  return segments[1] ?? null;
}

export function routeToPath(route: Route): string {
  return route === 'home' ? '/' : `/${route}`;
}

export const Topbar: React.FC<{
  route: Route;
  onNavigate: (to: string) => void;
}> = ({ route, onNavigate }) => {
  const feedbackUrl = 'https://github.com/dotnetprofessional/swe-devtools-support';

  const tabs: Array<{ route: Route; label: string }> = [
    { route: 'home', label: 'Dashboard' },
    { route: 'tools', label: 'Tools' },
    { route: 'editor', label: 'Editor' },
    { route: 'pipelines', label: 'Pipelines' },
    { route: 'settings', label: 'Settings' },
  ];

  return (
    <div className="h-12 border-b border-border flex items-center px-4 gap-3">
      <div className="flex items-center gap-2">
        <div className="font-semibold">SWE DevTools</div>
        <span className="px-2 py-0.5 rounded text-xs font-medium border border-border bg-muted/30 text-muted-foreground">
          Beta
        </span>
      </div>

      <nav className="flex items-center gap-1 ml-2">
        {tabs.map((t) => {
          const isActive = route === t.route;
          return (
            <Button
              key={t.route}
              variant="ghost"
              size="sm"
              className={isActive ? 'bg-accent text-accent-foreground' : ''}
              onClick={() => onNavigate(routeToPath(t.route))}
            >
              {t.label}
            </Button>
          );
        })}

        <Button
          variant="ghost"
          size="sm"
          onClick={() => window.open(feedbackUrl, '_blank', 'noreferrer')}
        >
          Feedback
        </Button>
      </nav>

      <div className="flex-1" />

      <Button
        variant="ghost"
        size="sm"
        className="h-8 w-8 p-0"
        title="Global preferences"
        onClick={() => onNavigate(routeToPath('settings'))}
      >
        <Settings className="h-4 w-4" />
      </Button>
    </div>
  );
};
